import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import useSwitch from "@react-hook/switch";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import type { FC } from "react";
import { useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import { Controller, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { ExternalLink } from "@/components/ExternalLink";
import { Modal, type ModalProps } from "@/components/Modal";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";
import { Trans } from "@/features/i18n/Trans";
import { invalidateCdhStatus } from "@/features/unify-scope-setting/invalidateCdhStatus";
import {
  ProfileUnificationWarning,
  useProductNotActivated,
} from "@/features/unify-scope-setting/ProfileUnificationWarning";
import { UnifyOrgsProvider } from "@/features/unify-scope-setting/ProfileUnificationWarning/Provider";
import { UnifyKeySelector } from "@/features/unify-scope-setting/UnifyKeySelector";
import { getDefaultUnifyKeys } from "@/features/unify-scope-setting/useDefaultUnifyKeys";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import CresclabImage from "@/statics/cresclab.png";

const cssIcon = css`
  font-size: 1rem;
  color: ${theme.colors.staticFgPlaceholder};
`;

const cssModalImageContainer = css`
  display: flex;
  justify-content: center;
  margin-bottom: 8px;
`;

const cssModalParagraph = css`
  margin-bottom: 16px;
`;

type UnifySettingFormValues = {
  unifyKeys: Array<InterludeTypes["UnificationKeys"]>;
};

const InstallCdhModal: FC<
  Pick<ModalProps, "open" | "handleClose"> & { onOk: () => void }
> = ({ open, handleClose, onOk }) => {
  const { t } = useTranslation();
  const notActivatedProducts = useProductNotActivated();
  const orgs = UnifyOrgsProvider.useUnifyOrgsContext();
  return (
    <Modal
      open={open}
      handleClose={handleClose}
      title={t("applications.cdh.modal.title")}
      footer={
        <Stack alignItems="flex-end">
          <Button
            onClick={onOk}
            variant="primary"
            disabled={notActivatedProducts.length !== 0}
          >
            {t("applications.cdh.modal.step1.button.nextStep")}
          </Button>
        </Stack>
      }
    >
      <>
        <div css={cssModalImageContainer}>
          <img src={CresclabImage} alt="Customer Data Hub" />
        </div>
        <Typography variant="body" css={cssModalParagraph}>
          {t("applications.cdh.modal.step1.content.paragraph")}
        </Typography>

        <ProfileUnificationWarning.UnifyScope orgs={orgs} />
        <ProfileUnificationWarning.RequireActivate />
      </>
    </Modal>
  );
};

/**
 * The connect button and its two-step modal. Used by the Applications card
 * and by the Profile unification page while the org is not connected.
 */
export const ConnectCdhButton: FC = () => {
  const { t } = useTranslation();
  const defaultUnifyKeys = getDefaultUnifyKeys();
  const form = useForm<UnifySettingFormValues>({
    mode: "all",
    defaultValues: {
      unifyKeys: defaultUnifyKeys,
    },
  });
  const [stepOneModalOpen, toggleStepOneModalOpen] = useSwitch(false);
  const [stepTwoModalOpen, toggleStepTwoModalOpen] = useSwitch(false);
  const [checkboxChecked, setCheckboxChecked] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const integrateCdhMutation = interlude.unifyScopeSetting.useIntegrateCdh();

  const goToStepTwo = useHandler(function goToStepTwo() {
    toggleStepOneModalOpen.off();
    toggleStepTwoModalOpen.on();
  });

  const handleCloseStepTwoModal = useHandler(
    function handleCloseStepTwoModal() {
      toggleStepTwoModalOpen.off();
      setCheckboxChecked(false);
      form.reset();
    },
  );

  const onSubmit = useHandler<SubmitHandler<UnifySettingFormValues>>(
    function handleSubmit(values) {
      if (integrateCdhMutation.isPending) return;

      integrateCdhMutation.mutate(
        {
          unifyKeys: values.unifyKeys,
        },
        {
          onSuccess: () => {
            enqueueSnackbar(t("common.updatedSuccessfully"), {
              variant: "success",
            });
            void invalidateCdhStatus(queryClient);
            navigate({
              to: "/data-hub",
              search: { tab: "rules" },
            });
          },
        },
      );
    },
  );

  const meInfoQuery = interlude.auth.useGetMe(undefined);

  const submit = useHandler(function submit() {
    form.handleSubmit(onSubmit)();
  });

  return (
    <>
      <Button
        variant="primary"
        onClick={toggleStepOneModalOpen.on}
        fullWidth={true}
      >
        {t("applications.cdh.button.integrate")}
      </Button>
      <InstallCdhModal
        open={stepOneModalOpen}
        onOk={goToStepTwo}
        handleClose={toggleStepOneModalOpen.off}
      />
      <Modal
        open={stepTwoModalOpen}
        handleClose={handleCloseStepTwoModal}
        title={t("applications.cdh.modal.title")}
        footer={
          <Stack direction="row" justifyContent="flex-end" gap="16px">
            <Button variant="plain" onClick={handleCloseStepTwoModal}>
              {t("common.cancel")}
            </Button>
            <Button
              variant="primary"
              onClick={submit}
              disabled={!checkboxChecked || integrateCdhMutation.isPending}
              loading={integrateCdhMutation.isPending}
            >
              {t("applications.cdh.modal.step2.button.integrate")}
            </Button>
          </Stack>
        }
      >
        <Stack gap="16px">
          <Stack gap="8px">
            <Typography variant="h2" fontWeight={700}>
              {t("applications.cdh.modal.step2.content.title")}
            </Typography>
            <Typography variant="body">
              <Trans
                i18nKey="applications.cdh.modal.step2.content.description"
                components={{
                  strong: <strong />,
                }}
                values={{
                  orgName: meInfoQuery.data?.organization.name ?? "",
                }}
              />
            </Typography>
            <Typography variant="body">
              <ExternalLink
                href={t("applications.cdh.modal.step2.unifyKey.learnMore.link")}
                trailingIcon={true}
              >
                {t("applications.cdh.modal.step2.unifyKey.learnMore")}
              </ExternalLink>
            </Typography>
          </Stack>
          <div>
            <Stack
              direction="row"
              gap="8px"
              alignItems="center"
              marginBottom="8px"
            >
              <Typography
                variant="body"
                fontWeight={500}
                color={theme.colors.staticFgTitle}
              >
                {t("applications.cdh.modal.step2.unifyKey")}
              </Typography>
              <MotifIcon un-i-motif="key" css={cssIcon} />
            </Stack>
            <Controller
              name="unifyKeys"
              control={form.control}
              rules={{ required: true }}
              render={({ field, formState }) => (
                <UnifyKeySelector
                  showCount
                  error={Boolean(formState.errors.unifyKeys)}
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  helperText={
                    !formState.errors.unifyKeys ? null : (
                      <Trans i18nKey="validation.unification.unifyKeys.min" />
                    )
                  }
                />
              )}
            />
          </div>
          <FormControlLabel
            control={
              <Checkbox
                checked={checkboxChecked}
                onChange={(e) => {
                  setCheckboxChecked(e.target.checked);
                }}
              />
            }
            label={
              <Typography variant="body">
                {t("applications.cdh.modal.step2.consensus")}
              </Typography>
            }
          />
        </Stack>
      </Modal>
    </>
  );
};
